import { lazy, Suspense } from 'react';
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
} from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';

/* ===== Layouts (lazy-loaded) ===== */
const FrontLayout = lazy(() => import('@/components/layouts/FrontLayout'));
const AdminLayout = lazy(() => import('@/components/layouts/AdminLayout'));

/* ===== Auth Guards (lazy-loaded) ===== */
const AdminAuthGuard = lazy(() => import('@/components/auth/AdminAuthGuard'));

/* ===== Front-end Pages (lazy-loaded) ===== */
const HomePage = lazy(() => import('@/pages/front/HomePage'));
const PlanListPage = lazy(() => import('@/pages/front/PlanListPage'));
const PlanDetailPage = lazy(() => import('@/pages/front/PlanDetailPage'));
const TeamBuildingPage = lazy(() => import('@/pages/front/TeamBuildingPage'));
const TravelPage = lazy(() => import('@/pages/front/TravelPage'));
const CulturePage = lazy(() => import('@/pages/front/CulturePage'));
const GalaPage = lazy(() => import('@/pages/front/GalaPage'));
const CaseListPage = lazy(() => import('@/pages/front/CaseListPage'));
const CaseDetailPage = lazy(() => import('@/pages/front/CaseDetailPage'));
const AboutPage = lazy(() => import('@/pages/front/AboutPage'));
const ContactPage = lazy(() => import('@/pages/front/ContactPage'));
const BlogListPage = lazy(() => import('@/pages/front/BlogListPage'));
const BlogDetailPage = lazy(() => import('@/pages/front/BlogDetailPage'));
const LoginPage = lazy(() => import('@/pages/front/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/front/RegisterPage'));
const UserCenterPage = lazy(() => import('@/pages/front/UserCenterPage'));
const MyOrdersPage = lazy(() => import('@/pages/front/MyOrdersPage'));
const OrderDetailPage = lazy(() => import('@/pages/front/OrderDetailPage'));
const SearchPage = lazy(() => import('@/pages/front/SearchPage'));

/* ===== Admin Pages (lazy-loaded) ===== */
const AdminLoginPage = lazy(() => import('@/pages/admin/AdminLoginPage'));
const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const AdminPlanListPage = lazy(() => import('@/pages/admin/AdminPlanListPage'));
const AdminPlanEditPage = lazy(() => import('@/pages/admin/AdminPlanEditPage'));
const AdminProductListPage = lazy(() => import('@/pages/admin/AdminProductListPage'));
const AdminCaseListPage = lazy(() => import('@/pages/admin/AdminCaseListPage'));
const AdminCaseEditPage = lazy(() => import('@/pages/admin/AdminCaseEditPage'));
const AdminOrderListPage = lazy(() => import('@/pages/admin/AdminOrderListPage'));
const AdminInquiryListPage = lazy(() => import('@/pages/admin/AdminInquiryListPage'));
const AdminContentPage = lazy(() => import('@/pages/admin/AdminContentPage'));
const AdminUserListPage = lazy(() => import('@/pages/admin/AdminUserListPage'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'));
const AdminTemplatePage = lazy(() => import('@/pages/admin/AdminTemplatePage'));
const AdminBannerPage = lazy(() => import('@/pages/admin/AdminBannerPage'));

/**
 * Loading fallback for Suspense boundaries.
 */
function LoadingFallback(): JSX.Element {
  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="60vh"
    >
      <CircularProgress size={40} sx={{ color: 'primary.main' }} />
    </Box>
  );
}

/**
 * Application router configuration.
 * - Front-end routes: /xxx (wrapped by FrontLayout)
 * - Admin routes: /admin/xxx (wrapped by AdminLayout)
 */
export function Router(): JSX.Element {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* ===== Front-end Routes ===== */}
          <Route element={<FrontLayout />}>
            <Route path="/" element={<HomePage />} />
            {/* Activity type dedicated pages */}
            <Route path="/teambuilding" element={<TeamBuildingPage />} />
            <Route path="/travel" element={<TravelPage />} />
            <Route path="/culture" element={<CulturePage />} />
            <Route path="/gala" element={<GalaPage />} />
            {/* Legacy redirect: /activities → / (home page) */}
            <Route path="/activities" element={<Navigate to="/" replace />} />
            <Route path="/activities/:typeId" element={<Navigate to="/" replace />} />
            {/* Product pages */}
            <Route path="/plans" element={<PlanListPage />} />
            <Route path="/plans/:id" element={<PlanDetailPage />} />
            {/* Other pages */}
            <Route path="/cases" element={<CaseListPage />} />
            <Route path="/cases/:id" element={<CaseDetailPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/guides" element={<BlogListPage />} />
            <Route path="/guides/:id" element={<BlogDetailPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/user" element={<UserCenterPage />} />
            <Route path="/user/orders" element={<MyOrdersPage />} />
            <Route path="/user/orders/:id" element={<OrderDetailPage />} />
            <Route path="/search" element={<SearchPage />} />
          </Route>

          {/* ===== Admin Routes ===== */}
          {/* Admin login — standalone, no layout wrapper */}
          <Route path="/admin/login" element={<AdminLoginPage />} />

          {/* Admin pages — protected by AdminAuthGuard */}
          <Route path="/admin" element={<AdminAuthGuard><AdminLayout /></AdminAuthGuard>}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboardPage />} />

            {/* Type-specific product management */}
            <Route path="teambuilding" element={<AdminProductListPage />} />
            <Route path="teambuilding/new" element={<AdminPlanEditPage />} />
            <Route path="teambuilding/:id/edit" element={<AdminPlanEditPage />} />
            <Route path="travel" element={<AdminProductListPage />} />
            <Route path="travel/new" element={<AdminPlanEditPage />} />
            <Route path="travel/:id/edit" element={<AdminPlanEditPage />} />
            <Route path="culture" element={<AdminProductListPage />} />
            <Route path="culture/new" element={<AdminPlanEditPage />} />
            <Route path="culture/:id/edit" element={<AdminPlanEditPage />} />
            <Route path="gala" element={<AdminProductListPage />} />
            <Route path="gala/new" element={<AdminPlanEditPage />} />
            <Route path="gala/:id/edit" element={<AdminPlanEditPage />} />

            {/* Legacy product management — shows all products */}
            <Route path="plans" element={<AdminPlanListPage />} />
            <Route path="plans/new" element={<AdminPlanEditPage />} />
            <Route path="plans/:id/edit" element={<AdminPlanEditPage />} />

            {/* Other admin pages */}
            <Route path="cases" element={<AdminCaseListPage />} />
            <Route path="cases/new" element={<AdminCaseEditPage />} />
            <Route path="cases/:id/edit" element={<AdminCaseEditPage />} />
            <Route path="orders" element={<AdminOrderListPage />} />
            <Route path="inquiries" element={<AdminInquiryListPage />} />
            <Route path="content" element={<AdminContentPage />} />
            <Route path="banners" element={<AdminBannerPage />} />
            <Route path="users" element={<AdminUserListPage />} />
            <Route path="settings" element={<AdminSettingsPage />} />
            <Route path="templates" element={<AdminTemplatePage />} />
          </Route>

          {/* ===== Catch-all: redirect to home ===== */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
